/*
 * Signature Installations — curated hub (Premium Pass, Jun 2026)
 * For You · Six worlds · Night · Soulumo Originals · Recently Added ·
 * Most Loved · All installations (search + A–Z). Curate first, catalogue second.
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import SoulumoHeader from "../../components/SoulumoHeader";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Search, ChevronRight, Loader2, Clock, ListOrdered, X, Moon } from "lucide-react";
import { WORLDS, NIGHT, ORIGINALS, subLabelFor, displayName } from "./signatureWorlds";
import { TOK as BASE, SERIF, SANS, Orb, SectionLabel, InstallRow, NAVY_BG } from "./SignatureBits";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const TOK = { ...BASE, goldMist: "rgba(215,187,122,0.28)" };
const HERO_ORB = "radial-gradient(circle at 50% 50%, rgba(255,241,201,0.55) 0%, rgba(215,187,122,0.28) 30%, rgba(215,187,122,0.06) 55%, rgba(13,31,58,0) 70%)";

const WorldTile = ({ w, onOpen, delay }) => (
  <button type="button" onClick={onOpen} data-testid={`signature-world-tile-${w.id}`}
    className="sig-rise relative overflow-hidden text-left rounded-3xl p-5 flex flex-col justify-end transition-[transform,border-color] duration-300 active:scale-[0.98]"
    style={{ minHeight: 176, background: "rgba(245,236,217,0.025)", border: `1px solid ${w.accent}33`, animationDelay: `${delay}ms`, isolation: "isolate", WebkitMaskImage: "-webkit-radial-gradient(white, black)" }}>
    <Orb gradient={w.orb} size={200} blur={false} className="absolute -top-20 -right-20" style={{ opacity: 0.8, animationDelay: `${delay * 3}ms` }} />
    <div className="relative">
      <div className="text-[1.35rem] font-light leading-[1.1]" style={{ fontFamily: SERIF, color: TOK.cream }}>{w.label}</div>
      <p className="mt-1.5 text-[0.78rem] italic leading-snug line-clamp-2" style={{ color: TOK.creamDim, fontFamily: SERIF }}>{w.tagline.split(".")[0]}.</p>
    </div>
  </button>
);

const Banner = ({ space, icon, title, line, onOpen, testId }) => (
  <button type="button" onClick={onOpen} data-testid={testId}
    className="sig-rise relative overflow-hidden w-full text-left rounded-3xl px-6 py-7 flex items-center gap-4 transition-transform duration-300 active:scale-[0.99]"
    style={{ background: "rgba(245,236,217,0.025)", border: `1px solid ${space.accent}40`, isolation: "isolate", WebkitMaskImage: "-webkit-radial-gradient(white, black)" }}>
    <Orb gradient={space.orb} size={220} blur={false} className="absolute -left-24 -top-8" style={{ opacity: 0.75 }} />
    <div className="relative flex-1 pl-16">
      <div className="text-[0.58rem] uppercase tracking-[0.32em] mb-1.5 inline-flex items-center gap-1.5" style={{ color: space.accent, fontFamily: SANS }}>{icon}{space.label}</div>
      <div className="text-[1.45rem] font-light leading-tight" style={{ fontFamily: SERIF, color: TOK.cream }}>{title}</div>
      <p className="mt-1 text-[0.85rem] italic" style={{ color: TOK.creamDim, fontFamily: SERIF }}>{line}</p>
    </div>
    <ChevronRight size={16} className="relative" color={space.accent} />
  </button>
);

const RowList = ({ rows, onOpen, onFav }) => rows.map((bp) => (
  <InstallRow key={bp.id} bp={bp} overline={subLabelFor(bp)} onOpen={() => onOpen(bp)} onFav={onFav} />
));

export default function SignatureInstallationsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [forYouIds, setForYouIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/audio-journeys/blueprints`, auth());
        if (cancelled) return;
        setItems(r.data.items || []);
        setCategories(r.data.categories || []);
        setForYouIds(r.data.for_you || []);
      } catch (e) {
        if (cancelled) return;
        setError("Couldn't load the Signature library.");
        console.error("[signature-installations]", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const catLabelById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.label])), [categories]);
  const catAuraById = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.aura || TOK.gold])), [categories]);
  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const curatedPool = useMemo(() => items.filter((i) => i.category !== "peace_humanity" && !i.overnight), [items]);
  const forYou = forYouIds.map((id) => byId[id]).filter(Boolean);
  const recentlyAdded = useMemo(() => curatedPool.slice(-3).reverse(), [curatedPool]);
  const mostLoved = useMemo(() => {
    const recent = new Set(recentlyAdded.map((i) => i.id));
    return curatedPool.filter((i) => !recent.has(i.id)).sort((a, b) => (b.loved_count || 0) - (a.loved_count || 0)).slice(0, 3);
  }, [curatedPool, recentlyAdded]);
  const humanFamily = items.find((i) => i.category === "peace_humanity");
  const favourites = useMemo(() => items.filter((i) => i.favourite), [items]);
  const setFav = (id, on) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, favourite: on } : x)));

  const searchResults = useMemo(() => {
    const qn = q.trim().toLowerCase();
    if (!qn) return null;
    return items.filter((i) =>
      [i.label, displayName(i), i.blurb, i.custom_concern, i.theme, i.category, catLabelById[i.category], subLabelFor(i)]
        .filter(Boolean).join(" ").toLowerCase().includes(qn));
  }, [items, q, catLabelById]);

  const openBp = (bp) => nav(`/signature-installations/${bp.category}?bp=${bp.id}`);
  const openSpace = (id) => nav(`/signature-installations/world/${id}`);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: NAVY_BG, backgroundAttachment: "fixed", color: TOK.cream, fontFamily: SANS }} data-testid="signature-installations-page">
      <SoulumoHeader />
      <Orb gradient={HERO_ORB} size={420} className="absolute -top-44 -right-40" />
      <div className="relative max-w-2xl mx-auto px-5 pt-5 pb-28">
        <button onClick={() => nav("/my-installations")} data-testid="signature-back-btn" aria-label="Back to Soulumo home"
          className="inline-flex items-center justify-center rounded-full"
          style={{ width: 44, height: 44, background: "rgba(13,31,58,0.4)", border: `1px solid ${TOK.goldMist}`, color: TOK.cream, backdropFilter: "blur(12px)" }}>
          <ChevronLeft size={18} />
        </button>
        <header className="mt-10 mb-12 sig-rise">
          <div className="text-[0.6rem] uppercase tracking-[0.34em] mb-3" style={{ color: TOK.goldSoft }}>Soulumo</div>
          <h1 className="text-4xl sm:text-5xl font-light leading-[1.05]" style={{ fontFamily: SERIF, color: TOK.cream }}>Signature Installations</h1>
          <p className="mt-3 text-lg italic" style={{ color: TOK.creamDim, fontFamily: SERIF }} data-testid="signature-tagline">
            Made for the moments that shape your inner world.
          </p>
        </header>

        {loading && (
          <div className="flex items-center gap-3 py-16 justify-center" style={{ color: TOK.creamDim }} data-testid="signature-loading">
            <Loader2 size={16} className="animate-spin" /> Gathering the library…
          </div>
        )}
        {error && <div className="py-10 text-center" style={{ color: "#dc8b6c" }} data-testid="signature-error">{error}</div>}

        {!loading && !error && (
          <>
            {favourites.length > 0 && (
              <section className="mb-14" data-testid="signature-favourites">
                <SectionLabel>Your favourites</SectionLabel>
                <RowList rows={favourites} onOpen={openBp} onFav={setFav} />
              </section>
            )}

            {forYou.length > 0 && (
              <section className="mb-14" data-testid="signature-for-you">
                <SectionLabel>For You</SectionLabel>
                <RowList rows={forYou} onOpen={openBp} onFav={setFav} />
              </section>
            )}

            <section className="mb-14" data-testid="signature-worlds">
              <SectionLabel>Choose your world</SectionLabel>
              <div className="grid grid-cols-2 gap-3" data-testid="signature-world-grid">
                {WORLDS.map((w, i) => <WorldTile key={w.id} w={w} delay={i * 70} onOpen={() => openSpace(w.id)} />)}
              </div>
            </section>

            <section className="mb-5">
              <Banner space={NIGHT} icon={<Moon size={10} />} title="Sleep & the night" line="Put the day down. Install while you rest."
                onOpen={() => openSpace("night")} testId="signature-night-entry" />
            </section>
            {humanFamily && (
              <section className="mb-14">
                <Banner space={ORIGINALS} title={humanFamily.label} line="Meeting the person before the label."
                  onOpen={() => openSpace("originals")} testId="signature-originals-entry" />
              </section>
            )}

            {recentlyAdded.length > 0 && (
              <section className="mb-14" data-testid="signature-recently-added">
                <SectionLabel>Recently Added</SectionLabel>
                <RowList rows={recentlyAdded} onOpen={openBp} />
              </section>
            )}
            {mostLoved.length > 0 && (
              <section className="mb-14" data-testid="signature-most-loved">
                <SectionLabel>Most Loved</SectionLabel>
                <RowList rows={mostLoved} onOpen={openBp} />
              </section>
            )}

            <section data-testid="signature-browse-all">
              <SectionLabel>All installations</SectionLabel>
              <div className="flex gap-2">
                <label className="flex-1 flex items-center gap-3 rounded-full px-4 py-2.5" style={{ background: "rgba(245,236,217,0.04)", border: `1px solid ${TOK.goldMist}` }}>
                  <Search size={14} style={{ color: TOK.goldSoft }} />
                  <input type="text" value={q} onChange={(e) => setQ(e.target.value)} data-testid="signature-search-input"
                    placeholder="Search installations…" className="flex-1 min-w-0 bg-transparent focus:outline-none text-[0.85rem]" style={{ color: TOK.cream }} />
                </label>
                <button type="button" onClick={() => setPickerOpen(true)} data-testid="signature-picker-open"
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 text-[0.62rem] uppercase tracking-[0.18em]"
                  style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.goldSoft, minHeight: 44 }}>
                  <ListOrdered size={13} /> A–Z
                </button>
              </div>
              {searchResults && (
                <div className="mt-4" data-testid="signature-search-results">
                  <div className="mb-1 text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: TOK.creamMist }}>
                    {searchResults.length} {searchResults.length === 1 ? "match" : "matches"}
                  </div>
                  {searchResults.length === 0
                    ? <p className="py-6 text-center italic" style={{ color: TOK.creamMist, fontFamily: SERIF }}>Nothing here yet. Try a different word.</p>
                    : <RowList rows={searchResults} onOpen={openBp} />}
                  {q.trim().length >= 8 && (
                    <button type="button" onClick={() => nav(`/my-journeys?explain=${encodeURIComponent(q.trim())}`)} data-testid="signature-search-coach"
                      className="mt-4 w-full rounded-2xl p-4 text-left" style={{ border: `1px solid ${TOK.gold}`, background: "rgba(215,187,122,0.08)" }}>
                      <span className="block text-lg" style={{ color: TOK.cream, fontFamily: SERIF }}>Create one for exactly this</span>
                      <span className="block text-xs" style={{ color: TOK.creamMist }}>Soulumo Coach will start from what you wrote</span>
                    </button>
                  )}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {pickerOpen && (
        <AlphabeticalPicker
          items={items}
          catLabelById={catLabelById}
          catAuraById={catAuraById}
          onClose={() => setPickerOpen(false)}
          onPick={(bp) => { setPickerOpen(false); openBp(bp); }}
        />
      )}
    </div>
  );
}

/* ── Alphabetical picker sheet ─────────────────────────────────
 * A tall, elegant bottom sheet (mobile) / centred card (sm+) that
 * lists every public installation A→Z, with a sticky A-Z alphabet
 * strip so the user can jump to any letter in one tap. Category
 * chip and duration are rendered inline. Search-filterable too. */
function AlphabeticalPicker({ items, catLabelById, catAuraById, onClose, onPick }) {
  const [filter, setFilter] = useState("");
  const containerRef = useRef(null);
  const letterRefs = useRef({});

  // Group sorted installations by first letter.
  const groups = useMemo(() => {
    const qn = filter.trim().toLowerCase();
    const filtered = items
      .filter((i) => !qn || (i.label || "").toLowerCase().includes(qn))
      .slice()
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    const buckets = {};
    for (const it of filtered) {
      const ltr = (it.label || "#").charAt(0).toUpperCase();
      const key = /[A-Z]/.test(ltr) ? ltr : "#";
      (buckets[key] ||= []).push(it);
    }
    return Object.keys(buckets)
      .sort()
      .map((letter) => ({ letter, items: buckets[letter] }));
  }, [items, filter]);

  const availableLetters = useMemo(() => new Set(groups.map((g) => g.letter)), [groups]);
  const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const jumpTo = (letter) => {
    const el = letterRefs.current[letter];
    const container = containerRef.current;
    if (!el || !container) return;
    // Use rect deltas — `offsetTop` is measured against the
    // offsetParent (a positioned ancestor of the scroll container,
    // not the container itself), so it over-shoots by the height
    // of the sticky filter+alphabet header. iter268 scar.
    const top =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      8;
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };

  // Close on ESC.
  useEffect(() => {
    const on = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [onClose]);

  const totalCount = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="signature-picker-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl flex flex-col"
        style={{
          background: `linear-gradient(180deg, rgba(215,187,122,0.06) 0%, ${TOK.ink} 40%)`,
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.cream,
          maxHeight: "88vh",
        }}
        data-testid="signature-picker-sheet"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between gap-3 px-5 py-4"
          style={{ borderBottom: `1px solid ${TOK.goldMist}` }}
        >
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.32em]" style={{ color: TOK.goldSoft }}>
              A – Z
            </div>
            <h3
              className="text-xl font-light leading-tight mt-0.5"
              style={{ fontFamily: '"Cormorant Garamond", serif' }}
            >
              Jump to installation
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="signature-picker-close"
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center rounded-full transition-all"
            style={{
              width: 44, height: 44,
              background: "rgba(245,236,217,0.04)",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.cream,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Filter */}
        <div className="px-5 py-3" style={{ borderBottom: `1px solid ${TOK.goldMist}` }}>
          <label
            className="flex items-center gap-3 rounded-full px-4 py-2 transition-all"
            style={{ background: "rgba(245,236,217,0.04)", border: `1px solid ${TOK.goldMist}` }}
          >
            <Search size={13} style={{ color: TOK.goldSoft }} />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="signature-picker-filter"
              placeholder="Filter by name…"
              autoFocus
              className="flex-1 bg-transparent focus:outline-none text-[0.85rem]"
              style={{ color: TOK.cream, fontFamily: '"Manrope", sans-serif' }}
            />
            <span className="text-[0.6rem] italic" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}>
              {totalCount}
            </span>
          </label>
        </div>

        {/* Body: alphabet rail + scrollable list */}
        <div className="flex-1 flex overflow-hidden">
          {/* A-Z jump rail */}
          <div
            className="flex flex-col items-center justify-start gap-0 py-3 px-1 overflow-y-auto"
            style={{
              borderRight: `1px solid ${TOK.goldMist}`,
              minWidth: 36,
              maxHeight: "100%",
              WebkitOverflowScrolling: "touch",
            }}
            data-testid="signature-picker-alphabet"
          >
            {ALPHABET.map((L) => {
              const has = availableLetters.has(L);
              return (
                <button
                  key={L}
                  type="button"
                  onClick={() => has && jumpTo(L)}
                  disabled={!has}
                  data-testid={`signature-picker-letter-${L}`}
                  aria-label={`Jump to ${L}`}
                  className="flex items-center justify-center text-[0.62rem] transition-colors"
                  style={{
                    width: 24, height: 22,
                    fontFamily: '"Manrope", sans-serif',
                    letterSpacing: "0.08em",
                    color: has ? TOK.gold : "rgba(245,236,217,0.22)",
                    cursor: has ? "pointer" : "default",
                  }}
                >
                  {L}
                </button>
              );
            })}
          </div>

          {/* Scrollable list */}
          <div
            ref={containerRef}
            className="flex-1 overflow-y-auto"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {groups.length === 0 ? (
              <div
                className="py-16 px-5 text-center italic"
                style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif' }}
                data-testid="signature-picker-empty"
              >
                Nothing matches that filter.
              </div>
            ) : (
              groups.map(({ letter, items: rows }) => (
                <section
                  key={letter}
                  ref={(el) => { letterRefs.current[letter] = el; }}
                  data-testid={`signature-picker-group-${letter}`}
                >
                  <div
                    className="sticky top-0 px-4 py-1.5 text-[0.62rem] uppercase tracking-[0.28em] z-10 backdrop-blur"
                    style={{
                      background: "rgba(13,31,58,0.88)",
                      borderBottom: `1px solid ${TOK.goldMist}`,
                      color: TOK.goldSoft,
                      fontFamily: '"Manrope", sans-serif',
                    }}
                  >
                    {letter}
                  </div>
                  <ul>
                    {rows.map((bp) => {
                      const aura = catAuraById[bp.category] || TOK.gold;
                      return (
                        <li key={bp.id}>
                          <button
                            type="button"
                            onClick={() => onPick(bp)}
                            data-testid={`signature-picker-row-${bp.id}`}
                            className="w-full text-left px-4 py-3 transition-all"
                            style={{
                              borderBottom: `1px solid ${TOK.goldMist}`,
                              color: TOK.cream,
                              minHeight: 60,
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex-1 min-w-0">
                                <div
                                  className="text-[1rem] font-light leading-snug truncate"
                                  style={{ fontFamily: '"Cormorant Garamond", serif' }}
                                >
                                  {bp.label}
                                </div>
                                <div className="mt-1 flex items-center gap-2 flex-wrap">
                                  <span
                                    className="text-[0.55rem] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full"
                                    style={{
                                      background: `${aura}18`,
                                      color: aura,
                                      border: `1px solid ${aura}44`,
                                      fontFamily: '"Manrope", sans-serif',
                                    }}
                                  >
                                    {catLabelById[bp.category] || bp.category}
                                  </span>
                                  <span
                                    className="inline-flex items-center gap-1 text-[0.6rem]"
                                    style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
                                  >
                                    <Clock size={10} /> {bp.duration_minutes} min
                                  </span>
                                </div>
                              </div>
                              <ChevronRight size={14} style={{ color: TOK.creamMist, marginTop: 6 }} />
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

